/**
 * The longest edge, in pixels, that the receipt analyzer will accept.
 *
 * <p>
 * Measured against the live model on 2026-09-03, not chosen. A 3024×4032 photo — an
 * ordinary iPhone portrait shot — is refused outright:
 * <code>max_tokens must be at least 1, got -3676</code>. The image tokenises into more than
 * the model's whole context window, leaving nothing to answer with. The same receipt read
 * correctly at 1512, 1200 and 768 px, so legibility is not the constraint here; context is.
 * </p>
 *
 * <p>
 * 1280 sits below the lowest size known to fail and well above the smallest known to work,
 * and a receipt at 1280px is comfortably legible — the 768px render still read its total
 * exactly. Raise it only against a measurement, never a guess.
 * </p>
 */
export const MAX_ANALYSIS_EDGE = 1280;

/** JPEG quality for the re-encode. High enough to keep small print sharp. */
const QUALITY = 0.85;

/**
 * Shrink a photo to something the analyzer can actually read.
 *
 * <p>
 * <b>Why the client and not the server.</b> The booth's other constraint is hall cellular:
 * a 4 MB photo over a busy hall is a slow upload before any model has seen it, and the
 * visitor is standing there watching. Resizing on the phone fixes the model's limit and the
 * upload together. The server remains the honest fallback — it degrades to "no suggestion"
 * rather than erroring — but every client at the booth is this app.
 * </p>
 *
 * <p>
 * <b>EXIF orientation is honoured</b> via <code>imageOrientation: "from-image"</code>.
 * Canvas otherwise draws the raw sensor pixels, so a portrait phone photo arrives at the
 * model rotated 90°, which is a poor way to ask it to read small print.
 * </p>
 *
 * <p>
 * Returns the ORIGINAL file untouched when it is already small enough — no needless
 * re-encode, and no generational quality loss on a photo that was fine. Also returns the
 * original if anything here fails: a browser without <code>createImageBitmap</code>, or a
 * canvas that will not encode. Degrading to today's behaviour beats losing the scan.
 * </p>
 */
export async function downscaleForAnalysis(file: File): Promise<Blob> {
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const longest = Math.max(bitmap.width, bitmap.height);

    if (longest <= MAX_ANALYSIS_EDGE) {
      bitmap.close();
      return file;
    }

    const scale = MAX_ANALYSIS_EDGE / longest;
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) return file;
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", QUALITY),
    );
    return blob ?? file;
  } catch {
    // A scan that reaches the model at full size may still fail; one that never leaves the
    // phone certainly does.
    return file;
  }
}
